import { useState } from "react";
import {
  fetchFacades,
} from "../../api/public.api.js";
import { useQuery } from "@tanstack/react-query";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { assetUrl, variantUrl } from "../../api/client.js";
import { Container } from "../../components/layout/Container.jsx";
import { PageHero } from "../../components/shared/PageHero.jsx";
import { ImageWithSkeleton } from "../../components/shared/ImageWithSkeleton.jsx";
import { Lightbox } from "../../components/designs/Lightbox.jsx";
import { SEO } from "../../lib/seo.jsx";

/**
 * /facades — one flat gallery of every facade. The backend no longer
 * groups facades by category, so this page is a single responsive image
 * grid with a lightbox.
 */
export default function FacadesPage() {
  const [lightbox, setLightbox] = useState({ images: [], index: -1 });

  const facadesQ = useQuery({
    queryKey: ["public", "facades"],
    queryFn: fetchFacades,
    staleTime: 5 * 60_000,
  });

  const facades = facadesQ.data ?? [];

  const openLightboxByFacade = (facade) => {
    const idx = facades.findIndex((f) => f._id === facade._id);
    setLightbox({
      images: facades.map((f) =>
        imageOrPlaceholder(f.image, { pool: "facade", key: f._id ?? f.slug ?? f.title })
      ),
      index: idx,
    });
  };

  return (
    <>
      <SEO
        title="Facades"
        description="Browse our full range of facade styles — pick the look that fits your block and street."
      />

      <PageHero
        eyebrow="Gallery"
        title="Facades"
        description="Customize the front of your home — explore every facade style we offer."
      />

      <Container className="py-12 lg:py-16">
        {facadesQ.isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/3] animate-pulse rounded-xl bg-bg-elevated" />
            ))}
          </div>
        ) : facades.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border-subtle bg-bg-elevated px-6 py-16 text-center text-text-secondary">
            Facades coming soon.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {facades.map((f) => {
              const key = f._id ?? f.slug ?? f.title;
              // Grid tile uses the 800px WebP variant for speed; falls
              // back to the original .jpg if the variant isn't on the
              // server yet (e.g. before optimize-images is run there).
              // Lightbox still loads the original for full-res viewing.
              const thumbSrc = f.image
                ? variantUrl(f.image, "md")
                : imageOrPlaceholder(null, { pool: "facade", key });
              const fallbackSrc = f.image ? assetUrl(f.image) : undefined;
              return (
                <button
                  key={f._id ?? f.slug}
                  type="button"
                  onClick={() => openLightboxByFacade(f)}
                  className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-border-subtle bg-bg-elevated"
                >
                  <ImageWithSkeleton
                    src={thumbSrc}
                    fallbackSrc={fallbackSrc}
                    onError={imgErrorFallback(key)}
                    alt={f.title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </button>
              );
            })}
          </div>
        )}
      </Container>

      {lightbox.index >= 0 && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          onChange={(i) => setLightbox((s) => ({ ...s, index: i }))}
          onClose={() => setLightbox({ images: [], index: -1 })}
        />
      )}
    </>
  );
}
